// Shared layout measurement.
//
// Extracted from ReaderView 2026-10-06 so the library can reuse it rather
// than growing a second measure-then-render race. The reader learned this
// the expensive way: `html, body, #root { height: 100dvh }` with nothing in
// the chain carrying an intrinsic height means ANY consumer can be handed a
// zero box, and 100dvh is the dynamic viewport — it is not reliably settled
// when a mount-time effect runs on a slow device.
//
// MEASURED: a stage with ~zero height made epub.js mount ten section views
// and collapse them to none, and currentLocation() returned null outright.
// A paginated library that computes items-per-page from a measured
// container is walking into exactly the same hazard, so it uses the same
// helper and the same degrade-to-something rule.

export const STAGE_WAIT_MAX_MS = 3000;

export interface StageWaitResult {
  w: number;
  h: number;
  waitedMs: number;
  timedOut: boolean;
  frames: number;
}

/** Resolve once `el` has a non-zero box, or after STAGE_WAIT_MAX_MS.
 *  ResizeObserver (fires as soon as layout lands) raced against rAF polling
 *  (covers browsers that do not fire the observer for a box that was
 *  already zero) and a hard timeout, so this can never hang. On timeout the
 *  caller is expected to render SOMETHING — never a blank screen. */
export function waitForStage(el: HTMLElement): Promise<StageWaitResult> {
  const t0 = Date.now();
  const measure = () => {
    const r = el.getBoundingClientRect();
    return { w: Math.round(r.width), h: Math.round(r.height) };
  };
  const first = measure();
  if (first.w > 0 && first.h > 0) {
    return Promise.resolve({ ...first, waitedMs: 0, timedOut: false, frames: 0 });
  }
  return new Promise<StageWaitResult>((resolve) => {
    let done = false;
    let frames = 0;
    let ro: ResizeObserver | null = null;
    let rafId = 0;
    let timer = 0;
    const finish = (timedOut: boolean) => {
      if (done) return;
      done = true;
      try { ro?.disconnect(); } catch { /* ignore */ }
      cancelAnimationFrame(rafId);
      window.clearTimeout(timer);
      resolve({ ...measure(), waitedMs: Date.now() - t0, timedOut, frames });
    };
    const check = () => {
      if (done) return;
      const m = measure();
      if (m.w > 0 && m.h > 0) finish(false);
    };
    const poll = () => {
      if (done) return;
      frames += 1;
      check();
      if (!done) rafId = requestAnimationFrame(poll);
    };
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(check);
      try { ro.observe(el); } catch { /* ignore */ }
    }
    rafId = requestAnimationFrame(poll);
    timer = window.setTimeout(() => finish(true), STAGE_WAIT_MAX_MS);
  });
}

/** Items that fit in a measured box, for a paginated view.
 *  Returns at least 1 so a mis-measure can never render an empty page —
 *  degrade to something, never to nothing. */
export function itemsPerPage(
  box: { w: number; h: number },
  item: { w: number; h: number },
  opts?: { minCols?: number; minRows?: number },
): { cols: number; rows: number; count: number; measured: boolean } {
  const measured = box.w > 0 && box.h > 0;
  const cols = measured
    ? Math.max(opts?.minCols ?? 1, Math.floor(box.w / Math.max(1, item.w)))
    : (opts?.minCols ?? 2);
  const rows = measured
    ? Math.max(opts?.minRows ?? 1, Math.floor(box.h / Math.max(1, item.h)))
    : (opts?.minRows ?? 3);
  return { cols, rows, count: Math.max(1, cols * rows), measured };
}
